import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Edit2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    getNotificationTemplates,
    createNotificationTemplate,
    updateNotificationTemplate,
} from "@/lib/api";
import { getErrorMessage } from "@/lib/error-messages";
import { getTemplateVariableInfo } from "@/lib/notification-template-variables";
import type { NotificationTemplate } from "@/types/domain";
import { Badge } from "@/components/ui/badge";

const templateSchema = z.object({
    name: z.string().min(1, "Name ist erforderlich"),
    slug: z.string().min(1, "Slug ist erforderlich").regex(/^[a-z0-9_-]+$/, "Ungültiger Slug (nur Kleinbuchstaben, Zahlen, Bindestriche und Unterstriche)"),
    subject: z.string().min(1, "Betreff ist erforderlich"),
    body: z.string().min(1, "Inhalt ist erforderlich"),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

export function AdminNotificationTemplatesPage() {
    const queryClient = useQueryClient();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);

    const { data: templates, isLoading } = useQuery({
        queryKey: ["notification-templates"],
        queryFn: getNotificationTemplates,
    });

    const form = useForm<TemplateFormValues>({
        resolver: zodResolver(templateSchema),
        defaultValues: {
            name: "",
            slug: "",
            subject: "",
            body: "",
        },
    });

    const saveMutation = useMutation({
        mutationFn: async (values: TemplateFormValues) => {
            const payload = {
                ...values,
                variables: getTemplateVariableInfo(values.slug).variables,
            };

            if (editingTemplate) {
                return updateNotificationTemplate(editingTemplate.id, payload);
            }
            return createNotificationTemplate(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notification-templates"] });
            toast.success(editingTemplate ? "Vorlage aktualisiert" : "Vorlage erstellt");
            setIsDialogOpen(false);
            setEditingTemplate(null);
            form.reset();
        },
        onError: (error) => toast.error(getErrorMessage(error)),
    });


    const handleEdit = (template: NotificationTemplate) => {
        setEditingTemplate(template);
        form.reset({
            name: template.name,
            slug: template.slug,
            subject: template.subject,
            body: template.body,
        });
        setIsDialogOpen(true);
    };

    const handleAdd = () => {
        setEditingTemplate(null);
        form.reset({
            name: "",
            slug: "",
            subject: "",
            body: "",
        });
        setIsDialogOpen(true);
    };

    const templateVariableInfo = getTemplateVariableInfo(form.watch("slug"));

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold">Vorlagen verwalten</h2>
                    <p className="text-sm text-muted-foreground">Erstelle und bearbeite E-Mail-Vorlagen.</p>
                </div>
                <Button onClick={handleAdd}>
                    <Plus className="mr-2 h-4 w-4" />
                    Neue Vorlage
                </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <Card key={i} className="animate-pulse">
                            <CardHeader className="h-24 bg-muted/50" />
                        </Card>
                    ))
                ) : templates?.map((template) => (
                    <Card key={template.id} className="flex flex-col">
                        <CardHeader>
                            <div className="flex justify-between items-start">
                                <CardTitle className="text-lg">{template.name}</CardTitle>
                                <Badge variant="secondary" className="font-mono text-[10px]">{template.slug}</Badge>
                            </div>
                            <CardDescription className="line-clamp-1">{template.subject}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex-1">
                            <div className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap mb-4">
                                {template.body}
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {getTemplateVariableInfo(template.slug).variables.map(v => (
                                    <Badge key={v} variant="outline" className="font-mono text-[10px]">
                                        {`{${v}}`}
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                        <CardContent className="pt-0 flex gap-2 border-t mt-auto py-4">
                            <Button variant="outline" size="sm" className="w-full" onClick={() => handleEdit(template)}>
                                <Edit2 className="mr-2 h-3.5 w-3.5" />
                                Bearbeiten
                            </Button>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingTemplate ? "Vorlage bearbeiten" : "Neue Vorlage"}</DialogTitle>
                        <DialogDescription>
                            Die verfügbaren Platzhalter werden vom Versand fest vorgegeben und beim Senden automatisch befüllt.
                        </DialogDescription>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(v => saveMutation.mutate(v))} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Name</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Kurs abgesagt" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="slug"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Slug</FormLabel>
                                            <FormControl>
                                                <Input placeholder="cancelled" {...field} disabled={!!editingTemplate} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <div className="space-y-2 rounded-lg border bg-muted/30 p-4">
                                <div className="space-y-1">
                                    <p className="text-sm font-medium">Verfügbare Variablen</p>
                                    <p className="text-sm text-muted-foreground">
                                        Diese Werte sind schreibgeschützt und ergeben sich aus der jeweiligen Versandlogik.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {templateVariableInfo.variables.map((variable) => (
                                        <Badge key={variable} variant="secondary" className="font-mono text-[10px]">
                                            {`{${variable}}`}
                                        </Badge>
                                    ))}
                                </div>
                                {templateVariableInfo.note && (
                                    <p className="text-xs text-muted-foreground">{templateVariableInfo.note}</p>
                                )}
                            </div>
                            <FormField
                                control={form.control}
                                name="subject"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Betreff</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Leider muss der Kurs {courseName} entfallen" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="body"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Inhalt</FormLabel>
                                        <FormControl>
                                            <Textarea placeholder="Hallo {name}, ..." className="min-h-[200px]" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                    )}
                                />
                            <DialogFooter>
                                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
                                <Button type="submit" disabled={saveMutation.isPending}>
                                    {saveMutation.isPending ? "Speichert..." : "Speichern"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>

        </div>
    );
}
